/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import { logout } from '../../features/user';
import {
  APP_NAME_SIMKEHILOT, APP_NAME_SIMEHADRIN, API_URL,
  getActiveAppName, getBrandLogo,
} from '../../Constants';
import cookie from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { getCommunityGeneralInfo } from '../../Helper';
import { useDispatch } from 'react-redux';
import { showErrorDialog } from '../../features/errorDialog';
import axios from 'axios';

const APP_NAME = getActiveAppName();

/* 3-state values for a boolean permission:
   'yes' → send true | 'no' → send false | 'ignore' → OMIT the key from the payload
   (the server treats a missing key as "inherit next in the chain, then default"). */
const PERM_YES = 'yes';
const PERM_NO = 'no';
const PERM_IGNORE = 'ignore';

/* Map a loaded permission value to its 3-state control value.
   true → yes | false → no | null/undefined (absent) → ignore. */
function toTriState(value) {
  if (value === true) return PERM_YES;
  if (value === false) return PERM_NO;
  return PERM_IGNORE;
}

/* Map a 3-state control value back to the stored permission value.
   yes → true | no → false | ignore → null (kept in state, OMITTED on save). */
function fromTriState(state) {
  if (state === PERM_YES) return true;
  if (state === PERM_NO) return false;
  return null;
}

/* ── permission settings ── */
const PERMISSIONS_META = [
  {
    key: 'MUST_SIGN_UP_FOR_CALL_FILTERING',
    label: 'שירות סינון שיחות',
    stateOn: 'מנויי הקהילה רשומים לסינון השיחות ומוגנים מצינתוקים',
    stateOff: 'מנויי הקהילה לא רשומים לשירות שיחות שיחות',
    about: 'שירות סינון השיחות מזהה שיחות נכנסות שמקורן בצינתוקים ובחיוגים אוטומטיים לא רצויים, וחוסם אותן — כך שהמנויים מקבלים רק שיחות אמיתיות.',
    type: 'boolean', accent: '#0E7490',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
      </svg>
    ),
  },
    {
    key: 'MAX_OFF_VALID_CALLS',
    label: 'הגדרת זמן השתקת סינון השיחות',
    desc: 'מספר הדקות המקסימלי שניתן לבטל בו את שירות אימות השיחות הנכנסות (באם ריק — לא מוגבל). לדוגמה, אם תזינו 60 — לא ניתן יהיה לבטל את האימות ליותר מ-60 דקות ברצף, ולאחר מכן הוא יחזור לפעול אוטומטית.',
    about: 'מנוי יכול להשהות זמנית את אימות השיחות הנכנסות. הגדרה זו קובעת את משך הזמן המקסימלי שבו ניתן להשהות את האימות, לפני שהוא חוזר לפעול אוטומטית.',
    type: 'number', accent: '#D97706',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
  },
  {
    key: 'CALL_FILTERING_AUTO_REGISTRATION',
    label: 'רישום שקט לסינון שיחות',
    recommended: true,
    desc: 'קובע האם מנוי חדש יירשם בשקט (אוטומטית) לשירות סינון השיחות הנכנסות בעת הצטרפותו לקהילה.',
    whenOn: 'כרגע מנוי חדש שמצטרף לקהילה אינו נרשם אוטומטית לסינון השיחות. בהפעלת ההגדרה, כל מנוי חדש יירשם אוטומטית לשירות כבר עם הצטרפותו — בלי שיצטרך לעשות זאת בעצמו.',
    whenOff: 'כרגע כל מנוי חדש נרשם אוטומטית לסינון השיחות. בכיבוי ההגדרה, מנויים חדשים לא יירשמו אוטומטית, ויצטרכו להצטרף לשירות באופן יזום.',
    stateOn: 'כל מנוי חדש נרשם אוטומטית לסינון השיחות בזמן ההצטרפות.',
    stateOff: 'מנויים חדשים אינם נרשמים אוטומטית — ההצטרפות לסינון נעשית ביוזמתם.',
    about: 'רישום שקט מצרף מנויים חדשים לשירות סינון השיחות באופן אוטומטי בעת הצטרפותם לקהילה, בלי שהם נדרשים לבצע פעולה כלשהי.',
    type: 'boolean', accent: '#059669',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
      </svg>
    ),
  },


  {
    key: 'BLCOK_HIDE_CALLERD_ID_IN_DIAL_IN',
    label: 'חסימת שיחות ממספר חסוי לכל הקהילה',
    desc: 'קובע האם לדחות אוטומטית שיחות נכנסות ממספר חסוי (ללא מזהה מתקשר).',
    whenOn: 'כרגע מנויי הקהילה יכולים לקבל שיחות גם ממספר חסוי (ללא מזהה). בהפעלת ההגדרה, שיחות נכנסות ממספר חסוי ייחסמו אוטומטית עבור כל המנויים.',
    whenOff: 'כרגע שיחות ממספר חסוי נחסמות עבור המנויים. בכיבוי ההגדרה, המנויים יוכלו שוב לקבל שיחות גם ממספרים חסויים.',
    stateOn: 'שיחות שמגיעות ממספר חסוי למנויי הקהילה ייחסמו אוטומטית',
    stateOff: 'מנוי הקהילה יכולים לקבל שיחות ממספר חסוי',
    about: 'השירות חוסם אוטומטית שיחות נכנסות שמגיעות ממספר חסוי עבור כל מנויי הקהילה',
    type: 'boolean', accent: '#DC2626',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    ),
  },
  
  {
    key: 'INCOMING_SMS_BLOCK',
    label: 'חסימת קבלת SMS',
    recommended: true,
    desc: 'קובע האם לחסום קבלת הודעות SMS נכנסות בסימים של מנויי הקהילה.',
    whenOn: 'כרגע המנויים מקבלים הודעות SMS כרגיל. בהפעלת ההגדרה, קבלת הודעות SMS תיחסם עבור כל מנויי הקהילה',
    whenOff: 'כרגע קבלת הודעות SMS חסומה אצל המנויים. בכיבוי ההגדרה, המנויים יוכלו לקבל הודעות SMS ללא הגבלה, ניתן להגדיר את שעות הפעילות במערכת השעון.',
    stateOn: 'כרגע המנויים לא יכולים לקבל הודעות SMS',
    stateOff: 'כרגע המנויים יכולים לקבל הודעות SMS',
    about: 'השירות חוסם קבלת הודעות SMS נכנסות בסימים של מנויי הקהילה.',
    type: 'boolean', accent: '#0891B2',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        <line x1="8" y1="10" x2="16" y2="10"/>
      </svg>
    ),
  },
  {
    key: 'DEFAULT_CALL_FILTERING_WITHOUT_MOBILES',
    label: 'מסנן שיחות ממספרים נייחים בלבד',
    desc: 'קובע האם מנויי הקהילה מצורפים אוטומטית לסינון השיחות, ללא סינון שיחות ממספרים ניידים (סלולריים).',
    whenOn: 'כרגע מערכת הסינון בודקת שיחות ממספרים נייחים בלבד. בהפעלת ההגדרה, המערכת תסנן גם שיחות ממספרים ניידים (סלולריים) שמזוהים כצינתוקים — הגנה רחבה ומחמירה יותר.',
    whenOff: 'כרגע המערכת מסננת גם שיחות ממספרים ניידים. בכיבוי ההגדרה, הסינון יחול על מספרים נייחים בלבד, ושיחות ממספרים ניידים יעברו למנויים ללא סינון.',
    stateOn: 'מערכת הסינון חוסמת גם שיחות ממספרים ניידים ודורשת מהם לעבור אימות לפני שהם עוברים למנוי',
    stateOff: 'מערכת הסינון חוסמת רק שיחות ממספרים נייחים, מספרים ניידים לא צריכים לעבור אימות',
    about: 'כדי להגביל את הסינון כך שלא יחסום שיחות ממספרים ניידים, כיוון שבדרך כלל הם לא צינתוקים, ויחסום רק מספרים נייחים תוכלו להפעיל שירות זה ',
    type: 'boolean', accent: '#2563EB',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.6"/>
        <line x1="3" y1="3" x2="21" y2="21"/>
      </svg>
    ),
  },
  {
    key: 'DIAL_111',
    label: 'חיוג לכוכבית 111',
    stateOn: 'מנויי הקהילה רשאים לחייג לכוכבית 111',
    stateOff: 'מנויי הקהילה חסומים לחיוג לכוכבית 111',
    about: 'הגדרה זו מאפשרת לחסום או לאפשר למנויי הקהילה לחייג לכוכבית 111, מערכת הניהול הטלפונית של המנוי האישי.',
    type: 'boolean', accent: '#0E7490',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
  },
  {
    key: 'OFF_VALID_IN_CALLS_IF_NOT_IL_NUMBER',
    label: 'ביטול אימות מתקשרים מיעדים לא ישראליים',
    stateOn: 'כרגע שיחות המגיעות ממספר לא ישראלי לא נדרשים לעבור אימות ',
    stateOff: 'כרגע שיחות המגיעות ממספר לא ישראלי נדרשים לעבור אימות ',
    about: 'שירות סינון שיחות דורש מהמחייג לעבור אימות לפני המעבר למנוי, בהפעלת ההגדרה מספרים לא ישראליי לא יידרשו לעבור אימות, כיוון שבדרך כלל הם לא שיחות צינתוק',
    type: 'boolean', accent: '#2563EB',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    key: 'BLOCK_USE_CUSTOMER_BLOCKS_REPORTS',
    label: 'חסימת שימוש במערכת דיווח המספרים',
    stateOn: 'המנויים לא יכולים לחייג למערכת הדיווח ב*112',
    stateOff: 'המנויים יכולים להשתמש במערכת דיווח המספרים.',
    type: 'boolean', accent: '#DC2626',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11H3v10h6V11zM21 3h-6v18h6V3zM15 7H9v14h6V7z"/><line x1="2" y1="2" x2="22" y2="22"/>
      </svg>
    ),
  },
  {
    key: 'SMS_TO_CALLING_FOR_FREE_FOR_NOT_HLR_ROUTE',
    label: 'העברת SMS נכנס בשיחה ללא עלות',
    stateOn: 'כל הודעת SMS שמגיעה למנוי תועבר אליו בשיחה קולית',
    stateOff: 'הודעות SMS שמגיעות למנוי לא יעברו אליו בשיחה קולית',
    about: 'כאשר שירות קבלת SMS מופעל, ומגיעה הודעת SMS ללקוח, הוא יכול לראות אותה במכשיר הטלפון שלו במידה והוא תומך בהודעות. אם תפעילו את ההגדרה גם לקוחות שאין להם מכשיר תומך יוכלו לקבל את ההודעה בשיחה קולית ',
    type: 'boolean', accent: '#0891B2',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h5"/>
      </svg>
    ),
  },
];

/* ── navigation tiles ── */
const TILES = [
  {
    label: 'מערכת שעון', sub: 'ניהול שעות וזמנים', path: '/clock-system',
    accent: '#D97706', grad: 'linear-gradient(135deg,#FBBF24,#F59E0B)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/>
      </svg>
    ),
  },
  {
    label: 'ניהול מנויים', sub: 'רשימת חברי הקהילה', path: '/customers-management',
    accent: '#2563EB', grad: 'linear-gradient(135deg,#60A5FA,#2563EB)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    label: 'ניהול חסימות', sub: 'הגדרות חסימת שיחות', path: '/blocks-management',
    accent: '#059669', grad: 'linear-gradient(135deg,#34D399,#059669)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <rect x="9" y="11" width="6" height="5" rx="1"/><path d="M10 11V9.5a2 2 0 014 0V11"/>
      </svg>
    ),
  },
  {
    label: 'חסימת צינטוקים', sub: 'הגדרת חסימת ופתיחת שיחות', path: '/anti-spam',
    accent: '#DC2626', grad: 'linear-gradient(135deg,#F87171,#DC2626)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="3" width="13" height="18" rx="2"/><line x1="8" y1="8" x2="13" y2="8"/><line x1="8" y1="12" x2="11" y2="12"/>
        <circle cx="17.5" cy="6.5" r="4.5"/><line x1="14.3" y1="3.3" x2="20.7" y2="9.7"/>
      </svg>
    ),
  },
];

const TILE_SECTION_BY_PATH = {
  '/clock-system': 'watch',
  '/customers-management': 'members',
  '/blocks-management': 'blocks',
  '/blocks-history': 'blocks',
  '/anti-spam': 'anti_spam',
};

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" className="db-spin">
      <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0" strokeOpacity="0.25"/>
      <path d="M21 12a9 9 0 00-9-9"/>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [stats,    setStats]    = useState({ activeLines: null, activeCustomers: null, blockListMode: { incoming: null, outcoming: null }, name: null, note: null });
  const [perms,    setPerms]    = useState(null);
  const [pending,  setPending]  = useState(null);   // {key,label,desc,type,icon,accent,to}
  const [numDraft, setNumDraft] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [success,  setSuccess]  = useState(false);

  /* community custom logo — shown below the name, stored locally per browser (no backend endpoint yet) */
  const [logo, setLogo] = useState(() => localStorage.getItem('community_logo') || '');
  const [logoError, setLogoError] = useState('');
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);
  const logoInputRef = useRef(null);

  const handleLogoPick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLogoError('');
    if (!file.type.startsWith('image/')) { setLogoError('יש לבחור קובץ תמונה'); return; }
    if (file.size > 1.5 * 1024 * 1024) { setLogoError('התמונה גדולה מדי (עד 1.5MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogo(dataUrl);
      try { localStorage.setItem('community_logo', dataUrl); } catch { setLogoError('לא ניתן לשמור את הלוגו'); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
    setLogoMenuOpen(false);
  };

  const handleLogoRemove = () => {
    setLogo('');
    localStorage.removeItem('community_logo');
    setLogoError('');
    setLogoMenuOpen(false);
  };

  useEffect(() => {
    const token = cookie.get('token');
    const h = { token };
    getCommunityGeneralInfo(token, t, dispatch, showErrorDialog, logout, navigate).then(s => {
      setStats(s);
      // מסנכרן את גישת הקהילה למקטעי הפורטל + מדיניות החסימה (לניווט/גארד/טפסים) מול הערך החי מ-getStat.
      localStorage.setItem('portal_access', JSON.stringify(s.portalAccess || {}));
      localStorage.setItem('block_list_policy', JSON.stringify(s.blockListPolicy || {}));
      // מקטע ההרשאות חסום לקהילה? לא קוראים ל-/permissions (יחזיר 403) ובלוק ההגדרות אינו מוצג.
      if (!s.portalAccess?.permissions) {
        axios.get(API_URL + '/permissions', { headers: h })
          .then(({ data }) => { if (data.responseStatus === 'OK') setPerms({ ...data.permissions }); })
          .catch(() => {});
      }
    });
  }, []); // eslint-disable-line

  const name = stats.name || (APP_NAME === APP_NAME_SIMKEHILOT ? 'סים קהילות' : APP_NAME) || '…';
  const enabledCount = perms ? Object.values(perms).filter(v => v === true).length : 0;

  /* supervision (kosher-body) info — a supervision community auto-owns every new subscriber whose number falls in these ranges */
  const supInfo = stats.supervisionInfo || null;
  const supMobilePrefixes = supInfo?.mobilePrefixes || [];
  const supLinePrefixes = supInfo?.linePrefixes || [];
  const supHasRanges = supMobilePrefixes.length > 0 || supLinePrefixes.length > 0;

  /* build a permission card from its meta key */
  const permCard = (key) => {
    const meta = PERMISSIONS_META.find(m => m.key === key);
    const { label, desc, about, whenOn, whenOff, stateOn, stateOff, accent, icon, type, recommended } = meta;
    // boolean perms are tri-state: true → פעיל, false → כבוי, null/undefined → לא מוגדר (unset)
    const boolStatus = () => {
      if (perms[key] === true) return { text: 'פעיל', cls: 'db-status--on' };
      if (perms[key] === false) return { text: 'כבוי', cls: 'db-status--off' };
      return { text: 'לא מוגדר', cls: 'db-status--unset' };
    };
    return {
      key, label, desc, about, whenOn, whenOff, stateOn, stateOff, accent, icon, type, recommended, value: perms[key],
      status: type === 'boolean'
        ? boolStatus()
        : { text: perms[key] != null ? String(perms[key]) : '∞', cls: 'db-status--num' },
    };
  };
  /* רשת של 3 עמודות, לפי שורות. מוצגות רק הגדרות שהקהילה רשאית לשנות בעצמה;
     בסים מהדרין מדיניות ה-SMS נאכפת במרוכז, ולכן שני כרטיסי ה-SMS לא מוצגים שם. */
  const isSimehadrin = APP_NAME === APP_NAME_SIMEHADRIN;

  const settingCards = perms ? [
    permCard('MUST_SIGN_UP_FOR_CALL_FILTERING'),
    permCard('BLCOK_HIDE_CALLERD_ID_IN_DIAL_IN'),
    permCard('CALL_FILTERING_AUTO_REGISTRATION'),
    ...(isSimehadrin ? [] : [permCard('INCOMING_SMS_BLOCK')]),
    permCard('DEFAULT_CALL_FILTERING_WITHOUT_MOBILES'),
    ...(isSimehadrin ? [] : [permCard('SMS_TO_CALLING_FOR_FREE_FOR_NOT_HLR_ROUTE')]),
    permCard('OFF_VALID_IN_CALLS_IF_NOT_IL_NUMBER'),
    permCard('MAX_OFF_VALID_CALLS'),
    permCard('DIAL_111'),
    permCard('BLOCK_USE_CUSTOMER_BLOCKS_REPORTS'),
  ] : [];

  const openEdit = (item) => {
    const it = { ...item, accent: '#0E7490' };   // unified teal in the modal
    // boolean perms open on a 3-state control preselecting the current state
    // (true → כן, false → לא, null/undefined → לא מוגדר).
    if (item.type === 'boolean') setPending({ ...it, tri: toTriState(item.value) });
    else { setNumDraft(item.value != null ? String(item.value) : ''); setPending({ ...it, tri: null }); }
  };

  // Build the payload sent to PUT /permissions from the (already updated) perms object:
  //  - boolean permissions in "לא מוגדר" (null/undefined) are OMITTED so the server
  //    inherits the next value in the chain; כן/לא are sent as true/false.
  //  - the numeric permission (MAX_OFF_VALID_CALLS) is sent as-is (null = ללא הגבלה).
  const buildPayload = (source) => {
    const payload = {};
    PERMISSIONS_META.forEach(({ key, type }) => {
      const value = source[key];
      if (type === 'boolean') {
        if (value === true || value === false) payload[key] = value; // omit on לא מוגדר (null/undefined)
      } else {
        payload[key] = value ?? null;
      }
    });
    return payload;
  };

  const confirmChange = async () => {
    if (!pending) return;
    const updated = { ...perms };
    if (pending.type === 'boolean') {
      // yes → true, no → false, ignore → null (kept in state, OMITTED from the payload)
      updated[pending.key] = fromTriState(pending.tri);
      // enabling "auto-join to call filter" also turns on "silent registration"
      // (the silent-registration toggle can still be turned off on its own afterwards)
      if (pending.key === 'MUST_SIGN_UP_FOR_CALL_FILTERING' && pending.tri === PERM_YES) {
        updated['CALL_FILTERING_AUTO_REGISTRATION'] = true;
      }
    } else {
      const n = numDraft === '' ? null : parseInt(numDraft, 10);
      updated[pending.key] = isNaN(n) ? null : n;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(API_URL + '/permissions', buildPayload(updated), { headers: { token: cookie.get('token') } });
      if (data.responseStatus === 'OK') {
        setPerms(updated);
        setPending(null);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2600);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="db-root">
      <span className="db-bsd">בס"ד</span>

      {/* ═══ RIGHT — GOLD COMMUNITY INFO PANEL ═══ */}
      <aside className="db-aside">
        <div className="db-aside-glow" />

        {/* identity */}
        <div className="db-identity">
          <div className="db-identity-logo">
            <img src={getBrandLogo()} alt={name} />
          </div>
          <div className="db-identity-welcome">ברוכים הבאים</div>
          <div className="db-identity-title">
            <span className="db-identity-pre">קהילת</span>
            <span className="db-identity-name">{name}</span>
          </div>
          {stats.networkName && (
            <div className="db-identity-network">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
              <span>שם הרשת: <strong>{stats.networkName}</strong></span>
            </div>
          )}

          {/* community type + nick */}
          <div className="db-identity-meta">
            {(stats.isSupervision || stats.communityType === 'SUPERVISION') ? (
              <span className="db-identity-type db-identity-type--supervision">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span>קהילת פיקוח</span>
              </span>
            ) : stats.communityType && (
              <span className="db-identity-type db-identity-type--regular">
                <span>קהילה רגילה</span>
              </span>
            )}
            {stats.nick && (
              <span className="db-identity-nick"> מזהה הנדסי של הקהילה: <strong> {stats.nick}</strong></span>
            )}
          </div>

          {/* community's own logo — circular profile-style avatar, click to edit */}
          <div className="db-comm-logo">
            <button
              type="button"
              className={'db-comm-avatar' + (logo ? '' : ' db-comm-avatar--empty')}
              title="לוגו הקהילה"
              onClick={() => setLogoMenuOpen(o => !o)}
            >
              {logo ? (
                <img src={logo} alt="לוגו הקהילה" />
              ) : (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              )}
              <span className="db-comm-avatar-overlay">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
              </span>
            </button>

            {!logo && (
              <span className="db-comm-logo-hint">העלה לוגו של הקהילה</span>
            )}

            {logoError && <div className="db-comm-logo-error">{logoError}</div>}
            <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoPick} />
          </div>
        </div>

        {/* stats — לא מוצגים כלל כשאין לקהילה גישה לרשימת המנויים (members חסום) */}
        <div className="db-aside-stats">
          {!stats.membersListHidden && <>
          <div className="db-astat">
            <div className="db-astat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
            </div>
            <div className="db-astat-val">{stats.activeLines != null ? stats.activeLines : <Spinner />}</div>
            <div className="db-astat-label">קווים פעילים</div>
            <span className="db-astat-pulse" />
          </div>

          <div className="db-astat">
            <div className="db-astat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
            </div>
            <div className="db-astat-val">{stats.activeCustomers != null ? stats.activeCustomers : <Spinner />}</div>
            <div className="db-astat-label">
              לקוחות שונים בקהילה
              <span className="db-astat-info" tabIndex={0} title="כל מי שמשויך לקהילה — לאו דווקא בעלי קו פעיל">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <span className="db-astat-tip">כולל את כל המנויים המשויכים לקהילה, גם ללא קו פעיל</span>
              </span>
            </div>
          </div>
          </>}
        </div>

        {/* logout */}
        <button className="db-logout" onClick={() => navigate('/logout')}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
          </svg>
          <span>התנתקות</span>
        </button>
      </aside>

      {/* ═══ MAIN ═══ */}
      <main className="db-main">

        {/* navigation tiles */}
        <div className="db-tiles">
          {TILES.filter(tile => !(stats.portalAccess && stats.portalAccess[TILE_SECTION_BY_PATH[tile.path]])).map((tile, i) => (
            <button key={tile.path} className="db-tile"
              style={{ '--accent': tile.accent, '--grad': tile.grad, animationDelay: `${i * 0.07}s` }}
              onClick={() => navigate(tile.path)}>
              <div className="db-tile-inner">
                <div className="db-tile-face db-tile-front">
                  <div className="db-tile-icon">{tile.icon}</div>
                  <div className="db-tile-label">{tile.label}</div>
                </div>
                <div className="db-tile-face db-tile-back">
                  <div className="db-tile-back-title">{tile.label}</div>
                  <div className="db-tile-back-sub">{tile.sub}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* supervision (kosher-body) panel — shown for supervision communities, above the settings */}
        {supInfo && (
          <div className="db-sup-panel">
            <div className="db-sup-panel-header">
              <div className="db-sup-panel-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div className="db-sup-panel-titles">
                <div className="db-sup-panel-label">{supInfo.label}</div>
                {(supInfo.fullName || supInfo.registrationId) && (
                  <div className="db-sup-panel-org">
                    {supInfo.fullName}
                    {supInfo.registrationId && <span className="db-sup-panel-reg">{supInfo.registrationId}</span>}
                  </div>
                )}
              </div>
              <span className="db-sup-panel-tag">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                קהילת פיקוח
              </span>
            </div>

            {supHasRanges && (
              <>
                <div className="db-sup-panel-note">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <span>
                    כל מנוי חדש במערכת שמספרו נכלל בטווחי המספור הבאים משויך <strong>אוטומטית</strong> לפיקוח של הקהילה.
                  </span>
                </div>
                <div className="db-sup-panel-ranges">
                  {supMobilePrefixes.length > 0 && (
                    <div className="db-sup-range-group">
                      <span className="db-sup-range-group-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2.5"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                        טווחי סלולר
                      </span>
                      <div className="db-sup-range-chips">
                        {supMobilePrefixes.map(p => (
                          <span key={p} className="db-sup-range">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {supLinePrefixes.length > 0 && (
                    <div className="db-sup-range-group">
                      <span className="db-sup-range-group-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                        טווחי מפ״א
                      </span>
                      <div className="db-sup-range-chips">
                        {supLinePrefixes.map(p => (
                          <span key={p} className="db-sup-range">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {stats.portalAccess?.permissions ? null : perms ? (
          <>
            <div className="db-set-tristate-hint">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>
                לכל הרשאה ניתן לבחור <strong>כן</strong> / <strong>לא</strong>, או <strong>לא מוגדר</strong> —
                במצב "לא מוגדר" הקהילה אינה קובעת דבר בהרשאה זו; ההחלטה עוברת לרמה הבאה ובסוף לברירת המחדל.
              </span>
            </div>
            <div className="db-set-grid">
              {settingCards.map((s, i) => (
                <div key={i}
                  className={`db-set-card db-set-card--clickable${(s.type === 'boolean' && s.value === true) ? ' db-set-card--on' : ''}${s.recommended ? ' db-set-card--recommended' : ''}`}
                  style={{ '--accent': '#0E7490', animationDelay: `${i * 0.04}s` }}
                  onClick={() => openEdit(s)}>
                  <div className="db-set-icon">{s.icon}</div>
                  <div className="db-set-label">
                    {s.label}
                    {s.recommended && (
                      <span className="db-set-recommended">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        מומלץ
                      </span>
                    )}
                  </div>
                  {/* boolean perms are tri-state (פעיל / כבוי / לא מוגדר), number perms show their value */}
                  <span className={`db-status ${s.status.cls}`}>{s.status.text}</span>
                </div>
              ))}
            </div>
        {/*
            <div className="db-set-hint">
              <span>מערכת הניהול של סים קהילות | גירסא V8.2</span>
            </div>
            */}
          </>
        ) : (
          <div className="db-loading-perms"><Spinner /> טוען הגדרות...</div>
        )}
      </main>

      {/* ═══ confirm-change modal ═══ */}
      {pending && (
        <div className="db-modal-overlay" onClick={() => !saving && setPending(null)}>
          <div className="db-modal" style={{ '--accent': pending.accent }} onClick={e => e.stopPropagation()}>
            <div className="db-modal-icon">{pending.icon}</div>
            <div className="db-modal-eyebrow">{pending.type === 'boolean' ? 'עריכת הגדרה' : 'עדכון ערך'}</div>
            <div className="db-modal-title">{pending.label}</div>

            {/* explanation about the service — tinted by the currently selected tri-state */}
            <div className={`db-modal-explain${pending.type === 'boolean' ? (pending.tri === PERM_YES ? ' db-modal-explain--on' : pending.tri === PERM_NO ? ' db-modal-explain--off' : ' db-modal-explain--unset') : ''}`}>
              <span className="db-modal-explain-dot" />
              <div className="db-modal-explain-body">
                {pending.type === 'boolean' ? (
                  <span className="db-modal-explain-now">
                    {pending.tri === PERM_YES
                      ? (pending.stateOn || pending.desc)
                      : pending.tri === PERM_NO
                        ? (pending.stateOff || pending.desc)
                        : 'הקהילה אינה קובעת דבר בהרשאה זו — ההחלטה עוברת לרמה הבאה בשרשרת ובסוף לברירת המחדל.'}
                  </span>
                ) : (
                  <span className="db-modal-explain-now">{pending.desc}</span>
                )}
              </div>
            </div>

            {/* 3-state control (כן / לא מוגדר / לא) for boolean permissions */}
            {pending.type === 'boolean' && (
              <div className="db-modal-tristate" role="radiogroup" aria-label={pending.label}>
                {[
                  { s: PERM_YES,    txt: 'כן' },
                  { s: PERM_IGNORE, txt: 'לא מוגדר' },
                  { s: PERM_NO,     txt: 'לא' },
                ].map(({ s, txt }) => {
                  const active = pending.tri === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`db-tristate-opt db-tristate-opt--${s}${active ? ' is-active' : ''}`}
                      onClick={() => setPending(p => ({ ...p, tri: s }))}
                    >
                      {txt}
                    </button>
                  );
                })}
              </div>
            )}

            {/* short "what is this service" explanation */}
            {(pending.about || pending.desc) && (
              <details className="db-modal-whatis">
                <summary>
                  <span className="db-modal-whatis-q">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    מה זה "{pending.label}"?
                  </span>
                  <svg className="db-modal-whatis-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </summary>
                <p className="db-modal-whatis-body">{pending.about || pending.desc}</p>
              </details>
            )}

            {pending.type !== 'boolean' && (
              <div className="db-modal-num">
                <label>ערך חדש</label>
                <input type="number" min={0} value={numDraft} placeholder="ללא הגבלה"
                  onChange={e => setNumDraft(e.target.value)} autoFocus />
              </div>
            )}

            <div className="db-modal-actions">
              <button className="db-modal-confirm" onClick={confirmChange} disabled={saving}>
                {saving ? <Spinner /> : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                )}
                {saving ? 'שומר...' : (pending.type === 'boolean' ? 'שמירה' : 'אישור')}
              </button>
              <button className="db-modal-cancel" onClick={() => setPending(null)} disabled={saving}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ community logo edit dialog ═══ */}
      {logoMenuOpen && (
        <div className="db-modal-overlay" onClick={() => setLogoMenuOpen(false)}>
          <div className="db-modal db-logo-modal" style={{ '--accent': '#C99700' }} onClick={e => e.stopPropagation()}>
            <button className="db-logo-modal-x" onClick={() => setLogoMenuOpen(false)} title="סגירה">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div className="db-logo-modal-preview">
              {logo ? <img src={logo} alt="לוגו הקהילה" /> : (
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              )}
            </div>
            <div className="db-modal-title">לוגו הקהילה</div>
            <div className="db-modal-desc">העלו לוגו אישי שיוצג בעמוד הבית של הקהילה, או הסירו את הלוגו הקיים.</div>
            <div className="db-logo-modal-actions">
              <button className="db-logo-modal-upload" onClick={() => logoInputRef.current && logoInputRef.current.click()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                {logo ? 'העלאת לוגו חלופי' : 'העלאת לוגו'}
              </button>
              {logo && (
                <button className="db-logo-modal-delete" onClick={handleLogoRemove}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  מחיקת הלוגו
                </button>
              )}
            </div>
            {logoError && <div className="db-comm-logo-error">{logoError}</div>}
          </div>
        </div>
      )}

      {/* ═══ success toast ═══ */}
      {success && (
        <div className="db-toast">
          <span className="db-toast-check">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </span>
          בוצע בהצלחה
        </div>
      )}
    </div>
  );
}
